import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@framework-controls/core';
import { JSXIf } from '@framework-controls/jsxconditions';
import { IconDashMenu, IconShoppingCart } from 'src/content/icons';
import css from './Menu.module.scss';
import classNames from 'classnames';
import MenuSlide from './MenuSlide/MenuSlide';
import { strings } from 'src/localization';
import { Appointment, Advisor, AdditionalServicesData, ApptCust, ApptVeh, SpecData, UpdateApptCustInp, UpdateApptVehInp, ApptOps, VehicleStockPhoto, PhotoList, OpType, DepartmentSpec, Customer, VehicleRec, ascMenuInteractionInput, ascCtaInteractionInput } from 'src/models';
import { IView } from 'Framework';
import MenuCart from './MenuCart/containers/MenuCart';
import { ComponentMapIds } from '../map';
import Logo from '../Logo/Logo';
import { checkCustUpdateEmail } from 'src/helpers/DepartmentHelper/DepartmentHelper';
import { ViewMode } from 'src/enums';
import MenuBackBtn from './MenuBackBtn/MenuBackBtn';
import { ascCtaInteraction, ascMenuInteraction, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import { IAppointmentState } from 'src/store/states';

interface IProps {
    back: () => void;
    appointment?: Appointment;
    allServices: AdditionalServicesData;
    apptCust?: ApptCust;
    apptVin?: string;
    apptVeh?: ApptVeh;
    apptOps?: ApptOps[];
    advisors?: Advisor[];
    viewMode?: ViewMode;
    vehiclePhoto?: VehicleStockPhoto;
    vehPhotoList?: PhotoList;
    theme: string;
    mainPaneViewOrder: IView[];
    deptSpecs: DepartmentSpec;
    errMsg: string;
    retCode: string;
    specProfile: string;
    store: string;
    showWeOwes?: boolean;
    showOEMRecall?: boolean;
    showPrvRecom?: boolean;
    branch: string;
    customerInfo: Customer;
    previewFlag: string;
    changeCustReturned: boolean;
    locale: string;
    skipServicesFlag?: boolean;
    profileOpen:boolean;
    newVehicleMileage: string,
    prevMileage: number;
    identityVerified: boolean;
    mfaEnabled: boolean;
    vehicleList: VehicleRec[];
    selectedVehicle: string;
    employeeFlag?: boolean;
    noAutoRecall: boolean;
    Itype: string | undefined;
    desktopOrMobile: string;
    countryCode: string;
    vwDeepLinkMileage: string;
    vwVariant: string,
    vwKeyword: string,
    vwProvider: string,
    appointmentState: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    setNoAutoRecall: (value: boolean) => void;
    pushToMainPanes?: (inputView: IView) => void;
    onApptCustUpdate?: (apptCust: UpdateApptCustInp) => void;
    updateApptVehicle?: (inp: UpdateApptVehInp) => void;
    clearError: () => void;
    changeCustInfo: (cust: Customer, profileNum, store: string, branch: string, previewFlag: string) => void;
    onCustUpdate: (cust: Customer) => void;
    checkUniqueEmail: (email: string, nameId: string, profileNum: string, store: string, branch: string,
        firstName: string, lastName: string, phone: string) => void;
    setChangeCustReturned: (value: boolean) => void;
    clearCheckedWebOps: () => void;
    clearCheckedAdditionalServices: () => void;
    setProfileOpen: (profileOpen: boolean) => void;
    selectVehicle: (inp: string) => void;
}

const Menu = (props: IProps) => {
    const [menuOpenFlag, setMenuOpenFlag] = useState<boolean>(false);
    const [cartOpenFlag, setCartOpenFlag] = useState<boolean>(false);
    const [hideMenuBtn, setHideMenuBtn] = useState<boolean>(false);
    const [hideMenuSlide, setHideMenuSlide] = useState<boolean>(false);
    const [hideCart, setHideCart] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(document.createElement('div'));
    const [hideBackBtn, setHideBackBtn] = useState<boolean>(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    let allowLogin = true;
    const brandFlags = [
        props.specData?.fordSmarttFlag,
        props.specData?.kiaTrackingFlag,
        // Add more brand tracking flags here in the future
    ];
    const hasAnyBrandFlag = brandFlags.some(Boolean);
    if (hasAnyBrandFlag) {
        allowLogin = false;
    }

    const vwDeepLinkParams = [
        props.vwDeepLinkMileage,
        props.vwVariant,
        props.vwKeyword,
        props.vwProvider,
    ];

    const vwDeepLinkData = vwDeepLinkParams.some(Boolean);

    useEffect(() => {
        const mainViewPanes = props.mainPaneViewOrder.map((view): IView => ({ ...view }));
        const currentViewPanes = mainViewPanes[mainViewPanes.length - 1];
        if ((currentViewPanes.viewId === ComponentMapIds.CustSearch) && (hasAnyBrandFlag || vwDeepLinkData)) {
            setHideBackBtn(true);
            setHideMenuBtn(true);
            setHideMenuSlide(true);
            setHideCart(true);

            return;
        }

        if (currentViewPanes.viewId === ComponentMapIds.Confirmation ||
            currentViewPanes.viewId === ComponentMapIds.ThankYou) {
            if (cartOpenFlag) {
                setCartOpenFlag(false);
            }
            if (menuOpenFlag) {
                setMenuOpenFlag(false);
            }

            setHideMenuBtn(true);
            setHideMenuSlide(true);
            setHideCart(true);
            setHideBackBtn(true);
        } else if (currentViewPanes.viewId === ComponentMapIds.SignUp) {
            setHideMenuSlide(true);
            setHideCart(true);
            setHideMenuBtn(true);
            setHideBackBtn(false);
        } else {
            setHideMenuSlide(false);
            setHideCart(false);
            setHideMenuBtn(false);
            setHideBackBtn(false);
        }
        // Dont display the back button if there is no screen to go back to.
        if (mainViewPanes.length === 1) {
            setHideBackBtn(true);
        }

        if (props.apptCust?.custFirstName === '' && props.apptVeh?.make === '') {
            if (cartOpenFlag) {
                setCartOpenFlag(false);
            }
            if (menuOpenFlag) {
                setMenuOpenFlag(false);
            }

            setHideMenuBtn(true);
            setHideMenuSlide(true);
            setHideCart(true);
        }

        // Prevents the Cart from being avaialble until the Select Services section is reached.
        if (
            currentViewPanes.viewId === ComponentMapIds.VehSel ||
            currentViewPanes.viewId === ComponentMapIds.VehLayout){
            setHideCart(true);
            setCartOpenFlag(false);
        }
    }, [props.mainPaneViewOrder, cartOpenFlag, menuOpenFlag, hideMenuSlide, hideCart, hideMenuBtn,
        hasAnyBrandFlag, props.apptCust, props.apptVeh, vwDeepLinkData]);

    // When the menu button is pressed, switch the open flag.
    const menuOpenHandler = useCallback((e:any) => {
        setMenuOpenFlag(menuOpenFlag => !menuOpenFlag);
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: strings.Menu,
            elementSubtype: 'hamburger_menu',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Menu,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'menu',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: !menuOpenFlag ? 'open' : 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [menuOpenFlag, props.selectedVehicle, props.vehicleList]);

    const cartOpenHandler = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setCartOpenFlag(cartOpenFlag => !cartOpenFlag);
        setAnchorEl(event.currentTarget);
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementSubtype: 'cta_icon',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const elementColor = getElementColor(event, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'menu',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: !cartOpenFlag ? 'open' : 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicleList, props.selectedVehicle);
    }, [cartOpenFlag, props.selectedVehicle, props.vehicleList]);


    const closeCartHandler = useCallback(() => {
        setCartOpenFlag(false);
    }, []);

    useEffect(() => {
        // IDs of the menu tray sections
        const sectionIds = ['ProfileFlyoutContent', 'VehicleFlyoutContent', 'CalendarFlyoutContent'];

        const handleDocumentClick = (event: any) => {
            // Check if user clicked within menu or specified sections.
            const isMenuClick = menuRef.current && menuRef.current.contains(event.target);
            const isSectionClick = sectionIds.some(id => {
                const sectionElement = document.getElementById(id);

                return sectionElement && sectionElement.contains(event.target);
            });

            // Close the menu only if the click is outside the menu and not on any of the sections
            if (!isMenuClick && !isSectionClick) {
                setMenuOpenFlag(false);
            }
        };

        let mobileClick = false;

        if (props.desktopOrMobile === 'M') {
            // Add the event listener when user clicks outside of the Menu.
            document.addEventListener('click', handleDocumentClick);
            mobileClick = true;
        }

        return () => {
            if (mobileClick) {
            // Remove the event listener
                document.removeEventListener('click', handleDocumentClick);
            }
        };
    }, [props.desktopOrMobile, setMenuOpenFlag]);


    // The menu button has a different look while the menu is open.
    // Therefore, toggle the css being used based on if the menu is open.
    const menuClassName = classNames(
        css.menuToggle,
        {
            [css.menuToggleCheck]: menuOpenFlag,
            [css.menuToggleCV]: props.theme === 'chevrolet',
        },
    );

    const jeepMenuClassName = classNames(
        css.menuToggleJeep,
        {
            [css.menuToggleCheckJeep]: menuOpenFlag,
        },
    );

    const kiaMenuClassName = classNames(
        css.menuToggleKia,
        {
            [css.menuToggleCheckKia]: menuOpenFlag,
        },
    );

    const toyotaSPMClassName = classNames(
        css.menuToggleToyotaSPM,
        {
            [css.menuToggleCheckToyotaSPM]: menuOpenFlag,
        },
    );

    const jaguarMenuClassName = classNames(
        css.menuToggleJaguar,
        {
            [css.menuToggleCheckJaguar]: menuOpenFlag,
        },
    );

    const miniMenuClassName = classNames(
        css.menuToggleMini,
        {
            [css.menuToggleCheckMini]: menuOpenFlag,
        },
    );

    const cadillacMenuClassName = classNames(
        css.menuToggleCadillac,
        {
            [css.menuToggleCheckCadillac]: menuOpenFlag,
        },
    );

    const hondaMenuClassname = classNames(
        css.menuToggle,
        {
            [css.menuToggleCheckHonda]: menuOpenFlag,
            [css.menuToggleHonda]: props.theme === 'honda',
        },
    );

    const buickMenuClassname = classNames(
        css.menuToggle,
        {
            [css.menuToggleCheck]: menuOpenFlag,
            [css.menuToggleBuick]: props.theme === 'buick',
        },
    );

    let menuClassNameSelect = menuClassName;
    let menuCSS = css.menu;
    let menuBtnCSS = css.menuBtn;

    switch (props.theme) {
        case 'jeep':
            menuClassNameSelect = jeepMenuClassName;
            break;
        case 'kia':
            menuClassNameSelect = kiaMenuClassName;
            menuCSS = css.menuKia;
            menuBtnCSS = css.menuBtnKia;
            break;
        case 'toyotaSPM':
            menuClassNameSelect = toyotaSPMClassName;
            break;
        case 'jaguar':
            menuClassNameSelect = jaguarMenuClassName;
            break;
        case 'mini':
            menuClassNameSelect = miniMenuClassName;
            break;
        case 'cadillac':
            menuClassNameSelect = cadillacMenuClassName;
            break;
        case 'honda':
            menuClassNameSelect = hondaMenuClassname;
            break;
        case 'buick':
            menuCSS = css.menuBuick;
            menuBtnCSS = css.menuBtnBuick;
            menuClassNameSelect = buickMenuClassname;
            break;
        default:
            break;
    }

    const cartClassName = classNames(
        css.cartBtn,
        {
            [css.cartToggleCheck]: cartOpenFlag,
            [css.cartToggleCheckBuick]: cartOpenFlag && props.theme === 'buick',
            [css.cartBtnKia]: props.theme === 'kia',
            [css.cartBtnCadillac]: props.theme === 'cadillac',
            [css.cartBtnBuick]: props.theme === 'buick',
        },
    );

    const dealerNameClassName = classNames(
        css.dealerName,
        {
            [css.dealerNameCadillac]: props.theme === 'cadillac',
            [css.dealerNameHonda]: props.theme === 'honda',
            [css.dealerNameBuick]: props.theme === 'buick',
        },
    );

    const dealerName = props.specData?.mobileName;

    const dealerNameLitClassName = classNames(
        css.dealerNameLit,
        {
            [css.dealerNameLitSmallFontSize]: dealerName && dealerName.length > 12,
            [css.dealerNameLitSmallAlt]: props.theme === 'cadillac' || props.theme ==='jaguar' ||
                props.theme === 'buick',
        },
    );

    const dealerNameArea = <h1 className={dealerNameLitClassName}>{dealerName}</h1>;

    const appointment: Appointment = props?.appointment ? props.appointment : {
        date: '',
        time: '',
        trans: '',
        advisor: '',
        advisorOptionEN: '',
        advisorOptionFR: '',
        department: '',
        advName: '',
        advImage: '',
        transFlag: '',
    };

    const showCart = props.apptVeh?.make ? true : false;
    let cartNum = props.apptOps?.length;
    if (cartNum && props.apptOps?.find(op => op.opType === OpType.SkipServiceReason)) {
        cartNum--;
    }
    const showCartNum = props.apptOps?.length && cartNum !== 0 ? true : false;

    const cartNumClassName = classNames(
        css.cartNum,
        {
            [css.cartNumDouble]: cartNum?.toString().length !== 1,
            [css.cartNumToggleCheck]: cartOpenFlag,
            [css.cartNumLM]: (props.theme === 'lexusMonogram' || props.theme === 'colorSensitive'),
        },
    );

    const noServicesHandler = (e: any) => {
        if (props.specData?.skipServicesFlag) {
            props.pushToMainPanes?.({
                viewId: ComponentMapIds.AddSkipServices,
                component: ComponentMapIds.AddSkipServices,
            });
        } else {
            let nextViewId = ComponentMapIds.AdditionalServices;
            let nextComponent = ComponentMapIds.AdditionalServices;
            const maintMake = props.selectedVehicle && props.selectedVehicle !== 'AddVehicle' ?
                props.vehicleList[props.selectedVehicle].make.toLowerCase().split(' ')[0]:'';
            // const maintContractView = (props.selectedVehicle && props.locale === 'en-US' &&
            const maintContractView = (props.selectedVehicle && props.countryCode === 'US' &&
            (((props.lexusMCLaunched || props.lexusTDMCLaunched) && maintMake==='lexus') ||
            ((props.toyotaMCLaunched || props.toyotaTDMCLaunched) && maintMake==='toyota')));
            if (!props.apptVeh?.vin || (!props.specData?.showWeOwes && !props.specData?.showPrvRecom &&
                (!props.specData?.showOEMRecall || !props.allServices.inquiryInvoked) && !maintContractView)) {
                nextViewId = ComponentMapIds.ServiceTab;
                nextComponent = ComponentMapIds.ServiceTab;
            }
            if (!props.allServices.recom[0] && !props.allServices.weOwes[0] &&
                !props.allServices.inquiryInvoked && !maintContractView) {
                nextViewId = ComponentMapIds.ServiceTab;
                nextComponent = ComponentMapIds.ServiceTab;
            }

            props.clearCheckedWebOps();
            props.clearCheckedAdditionalServices();
            props.pushToMainPanes?.({
                viewId: nextViewId,
                component: nextComponent,
            });
        }
        setCartOpenFlag(false);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ClickHere,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    };

    const currentMainPaneIndex = props.mainPaneViewOrder.length -1;
    const currentMainPane = props.mainPaneViewOrder[currentMainPaneIndex].viewId;

    // Display MenuCart if we have a vehicle.
    const menuCart = props.apptVeh?.make ?
        <MenuCart
            cartOpenFlag={cartOpenFlag}
            close={closeCartHandler}
            noServicesSelected={noServicesHandler}
            currentMainPane={currentMainPane}
            anchorEl={anchorEl}
        /> : null;

    let custEmailUpdate = checkCustUpdateEmail(props.appointment?.department, props.specData) || !props.customerInfo?.emailAddress;
    // Only allow the email to be updated in the menu if allowed in
    // all available spec departments.
    if (props.customerInfo.emailAddress) {
        const bdyEmlUpdate = props.specData?.bdySpecs ? props.specData?.bdySpecs?.custUpdateEml === 'Y' : true;
        const ossEmlUpdate = props.specData?.ossSpecs ? props.specData?.ossSpecs?.custUpdateEml === 'Y' : true;
        const svcEmlUpdate = props.specData?.svcSpecs ? props.specData?.svcSpecs?.custUpdateEml === 'Y' : true;
        custEmailUpdate = bdyEmlUpdate && ossEmlUpdate && svcEmlUpdate;
    }
    const transportation = props.appointment?.transFlag;
    const transIndex = props.specData?.Transportation?.find(trans => trans.type === transportation);

    const menuBtnContentsStyles = classNames(
        css.menuBtnContents,
        {
            [css.menuBtnContentsFord]: props.theme === 'ford',
        },
    );

    return (
        <div ref={menuRef}>
            <div className={menuCSS}>
                <div className={css.logoArea}>
                    <Logo specData={props.specData} theme={props.theme} menuLogo/>
                </div>

                {!hideMenuBtn ? <label className={menuBtnCSS}>
                    <span className={menuClassNameSelect}>
                        <Button
                            id='menuBtn'
                            onClick={(e:any) => menuOpenHandler(e)}
                            type='contained'
                            variant='menuBtn'
                            tooltip={strings.MenuTitle}
                        >
                            <div className={menuBtnContentsStyles}>
                                <div className={css.menuIcon}>
                                    <IconDashMenu />
                                </div>
                                <div className={css.menuLit}>
                                    {strings.Menu}
                                </div>
                            </div>
                        </Button>
                    </span>
                </label> : null}

                {!hideBackBtn ? <MenuBackBtn
                    menuOpen={menuOpenFlag}
                    extraPadding={!hideMenuBtn}
                    click={props.back}
                    theme={props.theme}
                /> : <div className={css.noBack}></div>}

                <div className={dealerNameClassName}>
                    {dealerNameArea}
                </div>

                {!hideMenuSlide ? <div className={css.slideArea}>
                    <MenuSlide
                        openFlag={menuOpenFlag}
                        cartFlag={cartOpenFlag}
                        appointment={appointment}
                        apptCust={props.apptCust}
                        apptVeh={props.apptVeh}
                        advisors={props.advisors}
                        cartHandler={cartOpenHandler}
                        pushToMain={props.pushToMainPanes}
                        onApptCustUpdate={props.onApptCustUpdate}
                        updateApptVehicle={props.updateApptVehicle}
                        theme={props.theme}
                        allowLogin={allowLogin}
                        custEmailUpdate={custEmailUpdate}
                        deptSpecs={props.deptSpecs}
                        clearError={props.clearError}
                        changeCustInfo={props.changeCustInfo}
                        setChangeCustReturned={props.setChangeCustReturned}
                        checkUniqueEmail={props.checkUniqueEmail}
                        onCustUpdate={props.onCustUpdate}
                        specProfile={props.specProfile}
                        store={props.store}
                        branch={props.branch}
                        errMsg={props.errMsg}
                        retCode={props.retCode}
                        customerInfo={props.customerInfo}
                        previewFlag={props.previewFlag}
                        changeCustReturned={props.changeCustReturned}
                        locale={props.locale}
                        transIndex={transIndex}
                        profileOpen={props.profileOpen}
                        setProfileOpen={props.setProfileOpen}
                        newVehicleMileage={props.newVehicleMileage}
                        prevMileage={props.prevMileage}
                        mainPaneViewOrder={props.mainPaneViewOrder}
                        identityVerified={props.identityVerified}
                        mfaEnabled={props.mfaEnabled}
                        vehicleList={props.vehicleList}
                        selectedVehicle={props.selectedVehicle}
                        employeeFlag={props.employeeFlag}
                        Itype={props.Itype}
                        desktopOrMobile={props.desktopOrMobile}
                        specData={props.specData}
                        sessionId = {props.sessionId}
                        lexusMCLaunched = {props.lexusMCLaunched}
                        lexusTDMCLaunched = {props.lexusTDMCLaunched}
                        toyotaMCLaunched = {props.toyotaMCLaunched}
                        toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                        appointmentState = {props.appointmentState}
                    />
                </div> : null}

                {!hideCart ? <div className={css.cartBtn}>
                    <div className={cartClassName}>
                        <JSXIf Condition={showCart}>
                            <Button
                                id='cartBtn'
                                onClick={cartOpenHandler}
                                type='contained'
                                tooltip={strings.CartTooltip}
                                variant='menuBtn'
                            >
                                <div
                                    className={css.cartIcon}>
                                    <IconShoppingCart />
                                </div>
                                <JSXIf Condition={showCartNum}>
                                    <div className={cartNumClassName}>{cartNum}</div>
                                </JSXIf>
                            </Button>
                        </JSXIf>
                    </div>
                </div> : null}
            </div>

            <JSXIf Condition={cartOpenFlag}>
                {menuCart}
            </JSXIf>
        </div>
    );
};

export default Menu;
